import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ScrollView, Platform, View, Keyboard, ActivityIndicator } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useScrollToTop } from 'expo-router/react-navigation';
import { BlurTargetView } from 'expo-blur';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';
import { Heading } from '@/components/gluestack/heading';
import { Card } from '@/components/gluestack/card';
import { Button, ButtonText, ButtonSpinner } from '@/components/gluestack/button';
import { Input, InputField, InputSlot, InputIcon } from '@/components/gluestack/input';
import { Divider } from '@/components/gluestack/divider';
import { Badge, BadgeText } from '@/components/gluestack/badge';
import { SettingsItem } from '@/components/ui/SettingsItem';
import { SettingsToggle } from '@/components/ui/SettingsToggle';
import { RangeSlider } from '@/components/ui/RangeSlider';
import { ContextSheet } from '@/components/ui/ContextSheet';
import MultiSelectSheet from '@/components/ui/MultiSelectSheet';
import LocationPlacePicker from '@/components/ui/LocationPlacePicker';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { SafeBlurView } from '@/components/ui/SafeBlurView';
import { CloseIcon } from '@/components/gluestack/icon';
import { MenuSelectButton } from '@/components/ui/MenuSelectButton';
import { SocialTagIcon } from '~/components/domain/common/SocialTagIcon';
import {
  Location03Icon,
  Navigation04Icon,
  FilterMailCircleIcon,
  CheckmarkSquare04Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { Search02Icon, UserAccountIcon } from '@hugeicons-pro/core-solid-rounded';
import { useAuth } from '~/components/providers/AuthProvider';
import { MembersService } from '~/services/domain/MembersService';
import { AccountService } from '~/services/domain/AccountService';
import {
  ISearchCardV6,
  SEARCH_CARD_TRANSLATION_MAP,
  createEmptySearchCardV6,
  isSearchCardEmpty,
} from '~/models/SearchCardV6';
import { PlaceSearchResult } from '@/components/providers/LocationProvider';
import {
  DEFAULT_BOOLEAN_ICON,
  DEFAULT_MULTI_SELECT_ICON,
  MEMBER_SEARCH_BOOLEAN_ICON_BY_FIELD,
  MEMBER_SEARCH_BOOLEAN_FILTER_FIELDS,
  MEMBER_SEARCH_MULTI_SELECT_ICON_BY_FIELD,
  MEMBER_SEARCH_MULTI_SELECT_FIELDS,
  MEMBER_SEARCH_MULTI_SELECT_SECTIONS,
  MEMBER_SEARCH_RANGE_FALLBACKS,
  MEMBER_SEARCH_RANGE_FIELDS,
  MULTI_SELECT_LABEL_KEY_BY_FIELD,
  type MultiSelectField,
  type RangeField,
} from '~/components/domain/members/memberSearchConfig';
import {
  useMeasurementPreferences,
  type WeightUnit,
  type HeightUnit,
} from '@/components/providers/MeasurementsProvider';
import { useToastMessages } from '@/hooks/useToastMessages';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { MyTypeFilterSummary } from './MyTypeFilterSummary';
import { useTheme } from '@/components/providers/ThemeProvider';

const SEARCH_PREFERENCES_KEY = 'communitySearchPreferences';

export type MemberSearchMode = 'search' | 'myTypeFilter';

interface ISearchPreference {
  searchCard: ISearchCardV6;
  locationDisplayName: string | null;
}

interface MemberSearchProps {
  mode?: MemberSearchMode;
  onSearch: (searchCard: ISearchCardV6) => void;
  onApply?: (searchCard: ISearchCardV6) => void;
  isSearching?: boolean;
  isApplying?: boolean;
  initialSearchCard?: ISearchCardV6 | null;
}

const SearchSection: React.FC<{
  title?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ title, children, className }) => (
  <VStack className={`gap-3 ${className ?? ''}`}>
    {title ? (
      <Heading size="md" className="ml-4 text-typography-700">
        {title}
      </Heading>
    ) : null}
    {children}
  </VStack>
);

const BOTTOM_BAR_HEIGHT = 72;
const MY_TYPE_FILTER_MINIMUM_RANGE_DISTANCE_PERCENT = 0.1;

export function MemberSearch({
  mode = 'search',
  onSearch,
  onApply,
  isSearching = false,
  isApplying = false,
  initialSearchCard,
}: MemberSearchProps) {
  const { t } = useTranslation('domain');
  const { t: tDefault } = useTranslation();
  const { showErrorToast } = useToastMessages();
  const { currentCommunity } = useAuth();
  const { effectiveColorScheme } = useTheme();
  const isAndroid = Platform.OS === 'android';
  const insets = useSafeAreaInsets();
  const isLargeScreen = useIsLargeScreen();
  const bottomBarExtraInset = isLargeScreen ? insets.bottom : 0;
  const isMyTypeFilterMode = mode === 'myTypeFilter';
  const {
    weightUnit,
    setWeightUnit: setGlobalWeightUnit,
    heightUnit,
    setHeightUnit,
  } = useMeasurementPreferences();

  const scrollViewRef = useRef<React.ComponentRef<typeof ScrollView>>(null);
  const blurTargetRef = useRef<View | null>(null);

  const renderMyTypeFilterInfoCard = useCallback(
    (className?: string) => {
      return (
        <Card
          size="md"
          variant="filled"
          className={`items-center justify-center gap-3 ${className ?? ''}`}>
          <HugeIconsWrapper icon={FilterMailCircleIcon} size={44} className="color-primary-500" />
          <Text className="w-full min-w-0 self-stretch text-center font-semibold text-typography-900">
            {tDefault('myTypeFilter.infoTitle')}
          </Text>
          <Text size="sm" className="w-full min-w-0 self-stretch text-center text-typography-600">
            {tDefault('myTypeFilter.infoDescription')}
          </Text>
        </Card>
      );
    },
    [tDefault]
  );

  const [searchCard, setSearchCard] = useState<ISearchCardV6>(createEmptySearchCardV6());
  const [activeMultiSelect, setActiveMultiSelect] = useState<MultiSelectField | null>(null);
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const [locationDisplayName, setLocationDisplayName] = useState<string | null>(null);
  const [isPreferencesLoaded, setIsPreferencesLoaded] = useState(false);
  const [isRangeDragging, setIsRangeDragging] = useState(false);
  const [summaryHeight, setSummaryHeight] = useState(0);
  const [showRestoredAlert, setShowRestoredAlert] = useState(true);
  const [hadRestoredCriteriaOnLoad, setHadRestoredCriteriaOnLoad] = useState(false);

  // Registers scroll to top for when the tab is pressed
  useScrollToTop(scrollViewRef);

  // Load saved search preferences for the current community on mount (only for search mode)
  useEffect(() => {
    // For My Type Filter mode, use the initialSearchCard prop if provided (restored notice never shown)
    if (isMyTypeFilterMode) {
      if (initialSearchCard) {
        setSearchCard({ ...createEmptySearchCardV6(), ...initialSearchCard });
      }
      setIsPreferencesLoaded(true);
      return;
    }

    if (!currentCommunity) {
      setIsPreferencesLoaded(true);
      return;
    }

    (async () => {
      try {
        const storedJson = await AsyncStorage.getItem(SEARCH_PREFERENCES_KEY);
        if (storedJson) {
          const allPreferences: Record<string, ISearchPreference> = JSON.parse(storedJson);
          const communityPrefs = allPreferences[currentCommunity];
          if (communityPrefs?.searchCard) {
            const merged = { ...createEmptySearchCardV6(), ...communityPrefs.searchCard };
            setSearchCard(merged);
            setLocationDisplayName(communityPrefs.locationDisplayName ?? null);
            if (!isSearchCardEmpty(merged)) {
              setHadRestoredCriteriaOnLoad(true);
            }
          }
        }
      } catch (e) {
        console.error('Failed to load search preferences', e);
      } finally {
        setIsPreferencesLoaded(true);
      }
    })();
  }, [currentCommunity, isMyTypeFilterMode, initialSearchCard]);

  // Save search preferences for the current community
  const saveSearchPreferences = useCallback(
    async (card: ISearchCardV6, locDisplayName: string | null) => {
      if (!currentCommunity) return;

      try {
        const storedJson = await AsyncStorage.getItem(SEARCH_PREFERENCES_KEY);
        const allPreferences: Record<string, ISearchPreference> = storedJson
          ? JSON.parse(storedJson)
          : {};

        allPreferences[currentCommunity] = {
          searchCard: card,
          locationDisplayName: locDisplayName,
        };

        await AsyncStorage.setItem(SEARCH_PREFERENCES_KEY, JSON.stringify(allPreferences));
      } catch (e) {
        console.error('Failed to save search preferences', e);
      }
    },
    [currentCommunity]
  );

  const { data: searchOptions, isPending: isLoadingOptions } = useQuery({
    queryKey: isMyTypeFilterMode ? ['myTypeFilterOptions'] : ['searchOptions'],
    queryFn: isMyTypeFilterMode
      ? AccountService.getMyTypeFilterOptions
      : MembersService.getSearchOptions,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  const translateTag = useCallback(
    (field: MultiSelectField, key: string): string => {
      const section = SEARCH_CARD_TRANSLATION_MAP[field];
      if (!section) return key;
      const translationKey = `ProfileTags.${section}.${key}`;
      const translated = t(translationKey, key);
      return translated === translationKey ? key : translated;
    },
    [t]
  );

  const getSelectedDisplay = useCallback(
    (field: MultiSelectField): string | React.ReactNode => {
      const values = searchCard[field];
      if (!values || values.length === 0) {
        return tDefault('search.anything', 'Anything');
      }

      const label =
        values.length === 1
          ? translateTag(field, values[0])
          : tDefault('search.multipleSelected', '{{count}} selected', { count: values.length });

      return (
        <Badge variant="solid" size="lg" action="muted" className="rounded-md bg-tertiary-500">
          <BadgeText className="font-bold normal-case color-white">{label}</BadgeText>
        </Badge>
      );
    },
    [searchCard, translateTag, tDefault]
  );

  const updateSearchCard = useCallback(
    <K extends keyof ISearchCardV6>(field: K, value: ISearchCardV6[K]) => {
      setSearchCard((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const patchSearchCard = useCallback((patch: Partial<ISearchCardV6>) => {
    setSearchCard((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleMultiSelectChange = useCallback(
    (field: MultiSelectField, keys: string[]) => {
      updateSearchCard(field, keys.length > 0 ? keys : null);
    },
    [updateSearchCard]
  );

  const handleRangeChange = useCallback(
    (field: RangeField, value: [number, number]) => {
      updateSearchCard(field, value);
    },
    [updateSearchCard]
  );

  const handleLocationSelect = useCallback(
    (place: PlaceSearchResult) => {
      patchSearchCard({
        Latitude: place.latitude,
        Longitude: place.longitude,
        IsNearMe: false,
      });
      setLocationDisplayName(place.displayName);
    },
    [patchSearchCard]
  );

  const handleIsNearMeToggle = useCallback(
    (value: boolean) => {
      if (value) {
        patchSearchCard({ IsNearMe: true, Latitude: null, Longitude: null });
        setLocationDisplayName(null);
      } else {
        updateSearchCard('IsNearMe', null);
      }
    },
    [patchSearchCard, updateSearchCard]
  );

  const clearLocation = useCallback(() => {
    patchSearchCard({ Latitude: null, Longitude: null, IsNearMe: null });
    setLocationDisplayName(null);
  }, [patchSearchCard]);

  const canClearLocation = useMemo(() => {
    return (
      searchCard.IsNearMe === true || searchCard.Latitude !== null || searchCard.Longitude !== null
    );
  }, [searchCard.IsNearMe, searchCard.Latitude, searchCard.Longitude]);

  const handleSearch = useCallback(() => {
    if (isSearchCardEmpty(searchCard)) {
      showErrorToast(
        tDefault(
          'search.selectAtLeastOneFilter',
          'Please select at least one filter to start your search'
        )
      );
      return;
    }
    onSearch(searchCard);
    saveSearchPreferences(searchCard, locationDisplayName);
  }, [onSearch, searchCard, locationDisplayName, saveSearchPreferences, showErrorToast, tDefault]);

  const clearAllFilters = useCallback(() => {
    const emptyCard = createEmptySearchCardV6();
    setSearchCard(emptyCard);
    setLocationDisplayName(null);
    setShowRestoredAlert(false);
    // Only persist to local storage in search mode
    if (!isMyTypeFilterMode) {
      saveSearchPreferences(emptyCard, null);
    }
  }, [isMyTypeFilterMode, saveSearchPreferences]);

  const rangeMetaByField = useMemo(() => {
    const meta = {} as Record<
      RangeField,
      { available: boolean; min: number; max: number; value: [number, number] }
    >;

    MEMBER_SEARCH_RANGE_FIELDS.forEach((field) => {
      const defaults = searchOptions?.[field];
      const available = Array.isArray(defaults) && defaults.length >= 2;
      const [min, max] = available
        ? [defaults[0], defaults[1]]
        : MEMBER_SEARCH_RANGE_FALLBACKS[field];

      const current = searchCard[field];
      const value: [number, number] =
        Array.isArray(current) && current.length >= 2 ? [current[0], current[1]] : [min, max];

      meta[field] = { available, min, max, value };
    });

    return meta;
  }, [searchCard, searchOptions]);

  const showRanges = useMemo(
    () => MEMBER_SEARCH_RANGE_FIELDS.some((f) => rangeMetaByField[f].available),
    [rangeMetaByField]
  );

  const formatNumberTrim = useCallback((value: number, decimals: number): string => {
    const fixed = value.toFixed(decimals);
    return fixed.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
  }, []);

  const formatWeight = useCallback(
    (value: number): string => {
      if (weightUnit === 'kg') {
        const kg = value * 0.45359237;
        return `${formatNumberTrim(kg, 1)} ${tDefault('profile.units.weight.suffix.kg', 'kg')}`;
      }
      if (weightUnit === 'st') {
        const st = value / 14;
        return `${formatNumberTrim(st, 1)} ${tDefault('profile.units.weight.suffix.st', 'st')}`;
      }
      return `${value} ${tDefault('profile.units.weight.suffix.lbs', 'lbs')}`;
    },
    [formatNumberTrim, tDefault, weightUnit]
  );

  const formatHeight = useCallback(
    (value: number): string => {
      if (heightUnit === 'm') {
        const meters = value / 100;
        return `${formatNumberTrim(meters, 2)} ${tDefault('profile.units.height.suffix.m', 'm')}`;
      }
      const inches = Math.round(value / 2.54);
      const feet = Math.floor(inches / 12);
      const remainingInches = inches % 12;
      return `${feet}'${remainingInches}"`;
    },
    [formatNumberTrim, heightUnit, tDefault]
  );

  const formatRangeValue = useCallback(
    (field: RangeField, value: number): string => {
      // Use field-specific formatting
      if (field === 'Weight') {
        return formatWeight(value);
      }
      if (field === 'Height') {
        return formatHeight(value);
      }

      // Default formatting for Age and BMI
      return value.toString();
    },
    [formatWeight, formatHeight]
  );

  const formatRangeDisplay = useCallback(
    (field: RangeField) =>
      (minValue: number, maxValue: number, formatValue: (v: number) => string) => {
        const meta = rangeMetaByField[field];
        if (!meta) return `${formatValue(minValue)} - ${formatValue(maxValue)}`;

        // Check if full range is selected
        if (minValue === meta.min && maxValue === meta.max) {
          return tDefault('search.anything', 'Anything');
        }

        // Check if only min is at boundary (e.g., "50 and above")
        if (minValue > meta.min && maxValue === meta.max) {
          const andAbove = tDefault('search.andAbove', 'and above');
          return `${formatValue(minValue)} ${andAbove}`;
        }

        // Check if only max is at boundary
        if (minValue === meta.min && maxValue < meta.max) {
          // For Age, always show full range (e.g., "18 to 25") to make minimum age clear
          // For other fields, use "and under" format (e.g., "150 lbs and under")
          if (field === 'Age') {
            const separator = tDefault('search.to', 'to');
            return `${formatValue(minValue)} ${separator} ${formatValue(maxValue)}`;
          } else {
            const andUnder = tDefault('search.andUnder', 'and under');
            return `${formatValue(maxValue)} ${andUnder}`;
          }
        }

        // Format with localized separator for range
        const separator = tDefault('search.to', 'to');
        return `${formatValue(minValue)} ${separator} ${formatValue(maxValue)}`;
      },
    [rangeMetaByField, tDefault]
  );

  const updateWeightUnit = useCallback(
    (unit: WeightUnit) => {
      setGlobalWeightUnit(unit);
    },
    [setGlobalWeightUnit]
  );

  const updateHeightUnit = useCallback(
    (unit: HeightUnit) => {
      setHeightUnit(unit);
    },
    [setHeightUnit]
  );

  const weightUnitLabel = useMemo(() => {
    if (weightUnit === 'kg') return tDefault('profile.units.weight.short.kg', 'kg');
    if (weightUnit === 'st') return tDefault('profile.units.weight.short.st', 'st');
    return tDefault('profile.units.weight.short.lb', 'lb');
  }, [tDefault, weightUnit]);

  const heightUnitLabel = useMemo(() => {
    if (heightUnit === 'm') return tDefault('profile.units.height.short.m', 'm');
    return tDefault('profile.units.height.short.ft', 'ft');
  }, [heightUnit, tDefault]);

  const availableBooleanFilters = useMemo(() => {
    if (!searchOptions) return [];
    return MEMBER_SEARCH_BOOLEAN_FILTER_FIELDS.filter(({ field }) => searchOptions[field] !== null);
  }, [searchOptions]);

  const optionsByField = useMemo(() => {
    const options = {} as Partial<Record<MultiSelectField, { key: string; label: string }[]>>;
    if (!searchOptions) return options;

    MEMBER_SEARCH_MULTI_SELECT_FIELDS.forEach(({ field }) => {
      const raw = searchOptions[field];
      if (!Array.isArray(raw) || raw.length === 0) {
        options[field] = [];
        return;
      }
      options[field] = raw.map((key) => ({ key, label: translateTag(field, key) }));
    });

    return options;
  }, [searchOptions, translateTag]);

  const activeOptions = useMemo(() => {
    if (!activeMultiSelect) return [];
    return optionsByField[activeMultiSelect] ?? [];
  }, [activeMultiSelect, optionsByField]);

  const activeSelectedKeys = useMemo(() => {
    if (!activeMultiSelect) return [];
    return (searchCard[activeMultiSelect] as string[] | null) ?? [];
  }, [activeMultiSelect, searchCard]);

  const locationLabel = useMemo(() => {
    if (searchCard.IsNearMe) {
      return tDefault('search.nearMe', 'Near me');
    }
    if (locationDisplayName) {
      return locationDisplayName;
    }
    if (searchCard.Latitude !== null && searchCard.Longitude !== null) {
      return tDefault('search.customLocation', 'Custom location');
    }
    return tDefault('search.anywhere', 'Anywhere');
  }, [
    searchCard.IsNearMe,
    searchCard.Latitude,
    searchCard.Longitude,
    locationDisplayName,
    tDefault,
  ]);

  const normalizeMyTypeFilterSearchCard = useCallback(
    (card: ISearchCardV6): ISearchCardV6 => {
      const normalized: ISearchCardV6 = { ...card };

      // If a range is set to its full/default bounds, treat it as "no filter".
      MEMBER_SEARCH_RANGE_FIELDS.forEach((field) => {
        const meta = rangeMetaByField[field];
        if (!meta?.available) return;
        const value = card[field];
        if (
          Array.isArray(value) &&
          value.length >= 2 &&
          value[0] === meta.min &&
          value[1] === meta.max
        ) {
          normalized[field] = null;
        }
      });

      // If a multi-select includes all available options, treat it as "Anything" (no filter).
      MEMBER_SEARCH_MULTI_SELECT_FIELDS.forEach(({ field }) => {
        const selected = card[field] as string[] | null | undefined;
        const availableOptions = optionsByField[field] ?? [];
        if (!Array.isArray(selected) || selected.length === 0 || availableOptions.length === 0)
          return;

        const selectedSet = new Set(selected);
        const includesAll =
          selectedSet.size === availableOptions.length &&
          availableOptions.every((opt) => selectedSet.has(opt.key));

        if (includesAll) {
          normalized[field] = null;
        }
      });

      return normalized;
    },
    [optionsByField, rangeMetaByField]
  );

  const handleApply = useCallback(() => {
    const cardToApply = isMyTypeFilterMode
      ? normalizeMyTypeFilterSearchCard(searchCard)
      : searchCard;
    onApply?.(cardToApply);
  }, [isMyTypeFilterMode, normalizeMyTypeFilterSearchCard, onApply, searchCard]);

  if (isLoadingOptions || !isPreferencesLoaded) {
    return (
      <VStack className="flex-1 items-center justify-center">
        <ActivityIndicator size="large" color="grey" />
      </VStack>
    );
  }

  return (
    <KeyboardAvoidingView behavior="padding" style={{ flex: 1 }}>
      <BlurTargetView ref={blurTargetRef} style={{ flex: 1 }}>
        <ScrollView
          ref={scrollViewRef}
          className="flex-1 bg-background-page"
          // Android-only: while a RangeSlider thumb is being dragged, disable ScrollView scrolling so
          // it doesn't steal the gesture (see RangeSlider's onBegin + minDistance(0) scroll-lock).
          // iOS/web should keep ScrollView scrolling enabled.
          scrollEnabled={isAndroid ? !isRangeDragging : true}
          keyboardShouldPersistTaps="handled">
          <VStack className="mx-auto w-full max-w-4xl gap-4 p-3 pt-4">
            {/* Restored Filters Alert */}
            {showRestoredAlert &&
            hadRestoredCriteriaOnLoad &&
            !isMyTypeFilterMode &&
            !isSearchCardEmpty(searchCard) ? (
              <Card size="md" variant="filled" className="gap-2">
                <HStack className="items-start gap-3">
                  <HugeIconsWrapper
                    icon={CheckmarkSquare04Icon}
                    size={20}
                    className="mt-0.5 color-warning-500"
                  />
                  <VStack className="flex-1 gap-1">
                    <Text size="sm" className="font-semibold text-typography-900">
                      {tDefault('search.restoredFiltersTitle', 'Filters restored')}
                    </Text>
                    <Text size="sm" className="text-typography-600">
                      {tDefault(
                        'search.restoredFiltersDescription',
                        'Your search filters from your last search have been restored. Want to start fresh? Click the reset button below.'
                      )}
                    </Text>
                  </VStack>
                </HStack>
              </Card>
            ) : null}

            {/* My Type Filter Info Card - Small screens (shown first, hidden on large) */}
            {isMyTypeFilterMode ? (
              <View className="lg:hidden">{renderMyTypeFilterInfoCard()}</View>
            ) : null}

            <View className="flex-col gap-4 lg:flex-row lg:gap-0">
              {/* Column 1: Ranges */}
              {showRanges ? (
                <SearchSection className="lg:flex-1">
                  <Card className="gap-4" size="md">
                    {MEMBER_SEARCH_RANGE_FIELDS.filter((f) => rangeMetaByField[f].available).map(
                      (field, index) => {
                        const meta = rangeMetaByField[field];
                        const label =
                          field === 'Age'
                            ? tDefault('search.age', 'Age')
                            : field === 'BMI'
                              ? tDefault('search.bmi', 'BMI')
                              : field === 'Weight'
                                ? tDefault('profile.weight', 'Weight')
                                : tDefault('profile.height', 'Height');

                        const isWeight = field === 'Weight';
                        const isHeight = field === 'Height';

                        return (
                          <React.Fragment key={field}>
                            {index > 0 ? <Divider /> : null}
                            <VStack className="gap-1">
                              <RangeSlider
                                label={label}
                                min={meta.min}
                                max={meta.max}
                                value={meta.value}
                                onValueChange={(value) => handleRangeChange(field, value)}
                                onDraggingChange={isAndroid ? setIsRangeDragging : undefined}
                                minimumDistancePercent={
                                  isMyTypeFilterMode
                                    ? MY_TYPE_FILTER_MINIMUM_RANGE_DISTANCE_PERCENT
                                    : undefined
                                }
                                formatValue={(value) => formatRangeValue(field, value)}
                                formatRangeDisplay={formatRangeDisplay(field)}
                                step={isWeight ? 5 : isHeight ? 1 : undefined}
                                valueDisplayMode="right"
                                labelAccessory={
                                  isWeight ? (
                                    <MenuSelectButton
                                      selectedKey={weightUnit}
                                      valueLabel={weightUnitLabel}
                                      options={[
                                        {
                                          key: 'lb',
                                          label: tDefault(
                                            'profile.units.weight.pounds',
                                            'Pounds (lb)'
                                          ),
                                        },
                                        {
                                          key: 'kg',
                                          label: tDefault(
                                            'profile.units.weight.kilograms',
                                            'Kilograms (kg)'
                                          ),
                                        },
                                        {
                                          key: 'st',
                                          label: tDefault(
                                            'profile.units.weight.stones',
                                            'Stones (st)'
                                          ),
                                        },
                                      ]}
                                      onSelect={(key) => updateWeightUnit(key as WeightUnit)}
                                      size="xs"
                                      variant="outline"
                                      action="neutral"
                                      className="gap-1 px-2"
                                      textClassName="text-typography-700"
                                      iconClassName="color-typography-700"
                                    />
                                  ) : isHeight ? (
                                    <MenuSelectButton
                                      selectedKey={heightUnit}
                                      valueLabel={heightUnitLabel}
                                      options={[
                                        {
                                          key: 'ft',
                                          label: tDefault('profile.units.height.feet', 'Feet (ft)'),
                                        },
                                        {
                                          key: 'm',
                                          label: tDefault(
                                            'profile.units.height.meters',
                                            'Meters (m)'
                                          ),
                                        },
                                      ]}
                                      onSelect={(key) => updateHeightUnit(key as HeightUnit)}
                                      size="xs"
                                      variant="outline"
                                      action="neutral"
                                      className="gap-1 px-2"
                                      textClassName="text-typography-700"
                                      iconClassName="color-typography-700"
                                    />
                                  ) : undefined
                                }
                              />
                            </VStack>
                          </React.Fragment>
                        );
                      }
                    )}
                  </Card>
                </SearchSection>
              ) : null}

              {showRanges ? (
                <Divider orientation="vertical" className="hidden self-stretch lg:mx-4 lg:flex" />
              ) : null}

              {/* Column 2: Username + Location (search mode) OR Info Card (myTypeFilter mode on large screens) */}
              {isMyTypeFilterMode ? (
                <VStack className="hidden gap-4 lg:flex lg:flex-1">
                  {renderMyTypeFilterInfoCard('flex-1')}
                </VStack>
              ) : (
                <VStack className="gap-4 lg:flex-1">
                  <SearchSection title={tDefault('search.username', 'Username')}>
                    <Card className="gap-3" size="md">
                      <Input variant="outline" className="bg-typography-0">
                        <InputSlot className="pl-3">
                          <HugeIconsWrapper
                            icon={UserAccountIcon}
                            size={16}
                            className="color-typography-700"
                          />
                        </InputSlot>
                        <InputField
                          placeholder={tDefault('search.usernamePlaceholder', 'Search by username')}
                          value={searchCard.UserName ?? ''}
                          onChangeText={(text) => updateSearchCard('UserName', text || null)}
                          autoCorrect={false}
                          autoCapitalize="none"
                          returnKeyType="done"
                          submitBehavior="blurAndSubmit"
                          onSubmitEditing={Keyboard.dismiss}
                        />
                        {searchCard.UserName ? (
                          <InputSlot
                            onPress={() => updateSearchCard('UserName', null)}
                            className="pr-3">
                            <InputIcon as={CloseIcon} />
                          </InputSlot>
                        ) : null}
                      </Input>
                    </Card>
                  </SearchSection>

                  <SearchSection title={tDefault('search.location', 'Location')}>
                    <Card className="gap-1" size="md">
                      <SettingsToggle
                        icon={Navigation04Icon}
                        label={tDefault('search.nearMe', 'Near me')}
                        value={searchCard.IsNearMe === true}
                        onValueChange={handleIsNearMeToggle}
                      />
                      <Divider />
                      <SettingsItem
                        label={locationLabel}
                        icon={Location03Icon}
                        onPress={
                          canClearLocation ? clearLocation : () => setIsLocationPickerOpen(true)
                        }
                        disabled={searchCard.IsNearMe === true}
                        rightIcon={canClearLocation ? null : undefined}
                        badge={
                          canClearLocation ? (
                            <Text className="font-semibold text-primary-500">
                              {tDefault('common.clear', 'Clear')}
                            </Text>
                          ) : null
                        }
                      />
                    </Card>
                  </SearchSection>
                </VStack>
              )}
            </View>

            {/* Multi-Select Filters */}
            {MEMBER_SEARCH_MULTI_SELECT_SECTIONS.map((section) => {
              const visibleFields = section.fields.filter(
                (field) => (optionsByField[field]?.length ?? 0) > 0
              );
              if (visibleFields.length === 0) return null;

              return (
                <SearchSection
                  key={section.key}
                  title={tDefault(section.titleKey, section.fallbackTitle)}>
                  <Card className="gap-1" size="md">
                    {visibleFields.map((field, index) => {
                      const labelKey = MULTI_SELECT_LABEL_KEY_BY_FIELD[field] ?? '';
                      return (
                        <React.Fragment key={field}>
                          {index > 0 && <Divider />}
                          <SettingsItem
                            label={tDefault(labelKey, field)}
                            badge={getSelectedDisplay(field)}
                            icon={
                              MEMBER_SEARCH_MULTI_SELECT_ICON_BY_FIELD[field] ??
                              DEFAULT_MULTI_SELECT_ICON
                            }
                            onPress={() => setActiveMultiSelect(field)}
                          />
                        </React.Fragment>
                      );
                    })}
                  </Card>
                </SearchSection>
              );
            })}

            {/* Boolean Filters */}
            {availableBooleanFilters.length > 0 && (
              <SearchSection title={tDefault('search.additional', 'Additional Filters')}>
                <Card className="gap-0" size="md">
                  {availableBooleanFilters.map(({ field, labelKey }, index) => (
                    <React.Fragment key={field}>
                      {index > 0 && <Divider />}
                      <SettingsToggle
                        icon={MEMBER_SEARCH_BOOLEAN_ICON_BY_FIELD[field] ?? DEFAULT_BOOLEAN_ICON}
                        label={tDefault(labelKey, field)}
                        value={searchCard[field] === true}
                        onValueChange={(v) => updateSearchCard(field, v)}
                      />
                    </React.Fragment>
                  ))}
                </Card>
              </SearchSection>
            )}

            {/* Bottom spacing for fixed buttons + home indicator / safe area */}
            <VStack style={{ height: BOTTOM_BAR_HEIGHT + summaryHeight + bottomBarExtraInset }} />
          </VStack>
        </ScrollView>
      </BlurTargetView>

      {/* Fixed Bottom Action Buttons */}
      <View className="absolute bottom-0 left-0 right-0">
        <SafeBlurView
          blurTarget={blurTargetRef}
          intensity={80}
          tint={effectiveColorScheme}
          blurMethod="dimezisBlurViewSdk31Plus"
          className="border-t border-outline-100"
          style={{ paddingBottom: bottomBarExtraInset }}>
          <MyTypeFilterSummary
            searchCard={searchCard}
            isEnabled={isMyTypeFilterMode && isPreferencesLoaded}
            onHeightChange={setSummaryHeight}
          />
          {isMyTypeFilterMode && summaryHeight > 0 && <Divider className="opacity-50" />}
          <HStack className="mx-auto w-full max-w-4xl gap-3 p-3">
            <Button variant="link" className="flex-1" onPress={clearAllFilters}>
              <ButtonText>{tDefault('search.clearAll', 'Reset')}</ButtonText>
            </Button>
            {isMyTypeFilterMode ? (
              <Button className="flex-1" onPress={handleApply} isDisabled={isApplying}>
                {isApplying && <ButtonSpinner className="mr-2" />}
                <ButtonText>{tDefault('myTypeFilter.apply')}</ButtonText>
              </Button>
            ) : (
              <Button className="flex-1" onPress={handleSearch} isDisabled={isSearching}>
                {isSearching ? (
                  <ButtonSpinner className="mr-2" />
                ) : (
                  <HugeIconsWrapper icon={Search02Icon} size={18} className="mr-2 text-white" />
                )}
                <ButtonText>{tDefault('search.search', 'Search')}</ButtonText>
              </Button>
            )}
          </HStack>
        </SafeBlurView>
      </View>

      {/* Multi-Select Sheet */}
      <ContextSheet
        isOpen={activeMultiSelect !== null}
        onClose={() => setActiveMultiSelect(null)}
        contentComponent={MultiSelectSheet}
        title={
          activeMultiSelect
            ? tDefault(MULTI_SELECT_LABEL_KEY_BY_FIELD[activeMultiSelect] ?? '', activeMultiSelect)
            : ''
        }
        payload={{
          options: activeOptions,
          selectedKeys: activeSelectedKeys,
          onSelectionChange: (keys: string[]) => {
            if (activeMultiSelect) {
              handleMultiSelectChange(activeMultiSelect, keys);
            }
          },
          renderOptionIcon:
            activeMultiSelect === 'SocialTags'
              ? (key: string) => (
                  <View style={{ width: 28, alignItems: 'flex-end' }}>
                    <SocialTagIcon tag={key} size={20} />
                  </View>
                )
              : undefined,
        }}
      />

      {/* Location Picker Sheet */}
      <ContextSheet
        isOpen={isLocationPickerOpen}
        onClose={() => setIsLocationPickerOpen(false)}
        contentComponent={LocationPlacePicker}
        title={tDefault('settings.location.pickPlace', 'Choose a place')}
        payload={{
          onSelect: handleLocationSelect,
        }}
      />
    </KeyboardAvoidingView>
  );
}
